/**
 * Mixed-funding M0 — allocation defaults and fail-closed validation.
 * Backend contract only; no client-record UI (Wave 5 / M0).
 */

export const SAH_ONLY_DEFAULT_ALLOCATION = {
  SUPPORT_AT_HOME: 1,
} as const;

export const NON_SAH_CLAIM_EXCLUDED_PROGRAMS = [
  'PRIVATE',
  'BROKERAGE',
  'TAC',
  'NDIS',
  'CHSP',
  'DVA',
] as const;

export type FundingProgramAllocation = Record<string, number>;

export type FundingAllocationValidation =
  | { ok: true; normalized: FundingProgramAllocation }
  | { ok: false; reason: string };

function finiteShare(value: unknown): number | null {
  const share =
    value && typeof value === 'object' && !Array.isArray(value)
      ? ((value as Record<string, unknown>).pct ??
        (value as Record<string, unknown>).percentage ??
        (value as Record<string, unknown>).allocation_pct)
      : value;
  const parsed = Number(share);
  return Number.isFinite(parsed) ? parsed : null;
}

export function normalizeFundingProgramAllocation(input: unknown): FundingProgramAllocation | null {
  if (!input || typeof input !== 'object' || Array.isArray(input)) return null;
  const normalized: FundingProgramAllocation = {};
  for (const [program, share] of Object.entries(input)) {
    const value = finiteShare(share);
    if (value == null || value < 0) return null;
    normalized[program] = value;
  }
  return Object.keys(normalized).length > 0 ? normalized : null;
}

export function validateFundingProgramAllocation(input: unknown): FundingAllocationValidation {
  const normalized = normalizeFundingProgramAllocation(input);
  if (!normalized) {
    return { ok: false, reason: 'Funding allocation is missing or malformed.' };
  }

  const allocationBasisPoints = Object.values(normalized).reduce(
    (sum, share) => sum + Math.round(share * 10_000),
    0,
  );
  if (Math.abs(allocationBasisPoints - 10_000) > 1) {
    const wholePercent = Math.trunc(allocationBasisPoints / 100);
    const fractionalPercent = allocationBasisPoints % 100;
    const percentLabel = `${wholePercent}.${String(fractionalPercent).padStart(2, '0')}`;
    return {
      ok: false,
      reason: `Funding allocation must sum to 100% (got ${percentLabel}%).`,
    };
  }

  return { ok: true, normalized };
}

export function resolveSahOnlyDefaultAllocation(
  fundingPrograms: readonly string[],
): FundingProgramAllocation | null {
  const programs = fundingPrograms.map((program) => String(program).toUpperCase());
  const hasSah = programs.includes('SUPPORT_AT_HOME') || programs.includes('HCP');
  const hasMixed = programs.some((program) => program !== 'SUPPORT_AT_HOME' && program !== 'HCP');
  if (!hasSah || hasMixed) return null;
  return { ...SAH_ONLY_DEFAULT_ALLOCATION };
}

export function sahClaimEligibleShare(allocation: FundingProgramAllocation): {
  sahShare: number;
  excludedPrograms: string[];
} {
  const excludedPrograms = Object.keys(allocation).filter((program) =>
    (NON_SAH_CLAIM_EXCLUDED_PROGRAMS as readonly string[]).includes(program),
  );
  const sahShare = allocation.SUPPORT_AT_HOME ?? 0;
  return { sahShare, excludedPrograms };
}

function isSimpleSahFundingPrograms(
  fundingPrograms: readonly string[] | null | undefined,
): boolean {
  if (!Array.isArray(fundingPrograms) || fundingPrograms.length === 0) return false;
  const programs = fundingPrograms.map((program) => String(program).trim().toUpperCase());
  const hasSah = programs.some((program) => program === 'SUPPORT_AT_HOME' || program === 'HCP');
  const hasNonSah = programs.some((program) => program !== 'SUPPORT_AT_HOME' && program !== 'HCP');
  return hasSah && !hasNonSah;
}

export function getSahClaimFundingAllocationBlocker(
  allocation: unknown,
  clientFundingPrograms?: readonly string[] | null,
): string | null {
  if (allocation == null) {
    return isSimpleSahFundingPrograms(clientFundingPrograms)
      ? null
      : 'Missing payer allocation: mixed or non-SAH clients require an explicit 100% Support at Home allocation before ACPP claiming.';
  }

  const validation = validateFundingProgramAllocation(allocation);
  if (!validation.ok) return validation.reason;

  const eligibility = sahClaimEligibleShare(validation.normalized);
  const hasOnlySupportAtHome =
    Math.abs(eligibility.sahShare - 1) <= 0.0001 && eligibility.excludedPrograms.length === 0;

  return hasOnlySupportAtHome
    ? null
    : 'Split or non-SAH payer allocation is not claimable through the Support at Home ACPP CSV path.';
}
